import { FC, useState, useRef, useEffect } from "react";
import {
  Settings,
  Trash2,
  Save,
  Plus,
  History,
  Undo2,
  Redo2,
  Play,
} from "lucide-react";
import * as Toolbar from "@radix-ui/react-toolbar";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  Box,
  Flex,
  TextField,
  Text,
  Button,
  Separator,
} from "@radix-ui/themes";
import { Edge } from "reactflow";
import { Workflow } from "@/types/breeze/Workflow";

interface WorkflowToolbarProps {
  workflow: Workflow;
  workflowName: string;
  workflowDescription: string;
  isLoading: boolean;
  hasUnsavedChanges: boolean;
  selectedEdge: Edge | null;
  canUndo: boolean;
  canRedo: boolean;
  onAddStep: () => void;
  onDeleteConnection: () => void;
  onSave: () => void;
  onTriggerWorkflow: () => void;
  onShowHistory: () => void;
  onShowSettings: () => void;
  onEnableWorkflow?: () => Promise<void>;
  onDisableWorkflow?: () => Promise<void>;
  onUndo: () => void;
  onRedo: () => void;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
}

export const WorkflowToolbar: FC<WorkflowToolbarProps> = ({
  workflow,
  workflowName,
  workflowDescription,
  isLoading,
  hasUnsavedChanges,
  selectedEdge,
  canUndo,
  canRedo,
  onAddStep,
  onDeleteConnection,
  onSave,
  onTriggerWorkflow,
  onShowHistory,
  onShowSettings,
  onEnableWorkflow,
  onDisableWorkflow,
  onUndo,
  onRedo,
  onNameChange,
  onDescriptionChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(workflowName);
  const [editedDescription, setEditedDescription] =
    useState(workflowDescription);
  const [isSaving, setIsSaving] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const descriptionTimeoutRef = useRef<NodeJS.Timeout>();

  // Update edited values when workflow changes
  useEffect(() => {
    setEditedName(workflowName);
    setEditedDescription(workflowDescription);
  }, [workflowName, workflowDescription]);

  // Handle name changes with debounce
  useEffect(() => {
    if (editedName !== workflowName) {
      setIsSaving(true);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        onNameChange(editedName);
        setIsSaving(false);
      }, 1000);
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [editedName, workflowName, onNameChange]);

  // Handle description changes with debounce
  useEffect(() => {
    if (editedDescription !== workflowDescription) {
      setIsSaving(true);
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
      descriptionTimeoutRef.current = setTimeout(() => {
        onDescriptionChange(editedDescription);
        setIsSaving(false);
      }, 1000);
    }
    return () => {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
    };
  }, [editedDescription, workflowDescription, onDescriptionChange]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditing]);

  return (
    <Flex
      direction="column"
      style={{
        backgroundColor: "var(--color-panel-solid)",
        borderBottom: "1px solid var(--gray-6)",
      }}
    >
      <Box p="2" style={{ borderBottom: "1px solid var(--gray-6)" }}>
        <Flex direction="column" gap="0">
          <TextField.Root
            ref={titleInputRef}
            value={editedName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEditedName(e.target.value)
            }
            onFocus={() => setIsEditing(true)}
            onBlur={() => setIsEditing(false)}
            placeholder="Untitled Workflow"
            size="2"
            style={{
              fontSize: "18px",
              fontWeight: "500",
              border: "none",
              background: "transparent",
            }}
            variant="soft"
          />
          <TextField.Root
            ref={descriptionInputRef}
            value={editedDescription}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEditedDescription(e.target.value)
            }
            placeholder="Add a description..."
            size="1"
            style={{
              border: "none",
              background: "transparent",
              marginLeft: "2.5px",
            }}
            variant="soft"
          />
        </Flex>
      </Box>

      <Tooltip.Provider>
        <Toolbar.Root asChild>
          <Flex align="center" gap="2" p="1">
            <Flex align="center" gap="3" px="1">
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Toolbar.Button asChild>
                    <Button variant="ghost" size="1" onClick={onAddStep}>
                      <Plus size={16} />
                    </Button>
                  </Toolbar.Button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    style={{
                      backgroundColor: "var(--gray-12)",
                      color: "var(--gray-1)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                    sideOffset={5}
                  >
                    Add Step
                    <Tooltip.Arrow style={{ fill: "var(--gray-12)" }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>

              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Toolbar.Button asChild>
                    <Button
                      variant="ghost"
                      size="1"
                      onClick={onDeleteConnection}
                      disabled={!selectedEdge}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </Toolbar.Button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    style={{
                      backgroundColor: "var(--gray-12)",
                      color: "var(--gray-1)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                    sideOffset={5}
                  >
                    Delete Connection
                    <Tooltip.Arrow style={{ fill: "var(--gray-12)" }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Flex>

            <Separator
              orientation="vertical"
              size="1"
              style={{ margin: "0 4px", height: "24px" }}
            />

            <Flex align="center" gap="3" px="1">
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Toolbar.Button asChild>
                    <Button
                      variant="ghost"
                      size="1"
                      onClick={onUndo}
                      disabled={!canUndo}
                    >
                      <Undo2 size={16} />
                    </Button>
                  </Toolbar.Button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    style={{
                      backgroundColor: "var(--gray-12)",
                      color: "var(--gray-1)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                    sideOffset={5}
                  >
                    Undo
                    <Tooltip.Arrow style={{ fill: "var(--gray-12)" }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>

              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Toolbar.Button asChild>
                    <Button
                      variant="ghost"
                      size="1"
                      onClick={onRedo}
                      disabled={!canRedo}
                    >
                      <Redo2 size={16} />
                    </Button>
                  </Toolbar.Button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    style={{
                      backgroundColor: "var(--gray-12)",
                      color: "var(--gray-1)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                    sideOffset={5}
                  >
                    Redo
                    <Tooltip.Arrow style={{ fill: "var(--gray-12)" }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Flex>

            <Separator
              orientation="vertical"
              size="1"
              style={{ margin: "0 4px", height: "24px" }}
            />

            <Flex align="center" gap="3" px="1">
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Toolbar.Button asChild>
                    <Button
                      variant="ghost"
                      size="1"
                      onClick={onSave}
                      disabled={isLoading || !hasUnsavedChanges}
                    >
                      <Save size={16} />
                    </Button>
                  </Toolbar.Button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    style={{
                      backgroundColor: "var(--gray-12)",
                      color: "var(--gray-1)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                    sideOffset={5}
                  >
                    Save Workflow
                    <Tooltip.Arrow style={{ fill: "var(--gray-12)" }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>

              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Toolbar.Button asChild>
                    <Button
                      variant="ghost"
                      size="1"
                      onClick={onTriggerWorkflow}
                      disabled={isLoading}
                    >
                      <Play size={16} />
                    </Button>
                  </Toolbar.Button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    style={{
                      backgroundColor: "var(--gray-12)",
                      color: "var(--gray-1)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                    sideOffset={5}
                  >
                    Test Workflow
                    <Tooltip.Arrow style={{ fill: "var(--gray-12)" }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>

              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Toolbar.Button asChild>
                    <Button variant="ghost" size="1" onClick={onShowHistory}>
                      <History size={16} />
                    </Button>
                  </Toolbar.Button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    style={{
                      backgroundColor: "var(--gray-12)",
                      color: "var(--gray-1)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                    sideOffset={5}
                  >
                    View Job History
                    <Tooltip.Arrow style={{ fill: "var(--gray-12)" }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>

              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Toolbar.Button asChild>
                    <Button variant="ghost" size="1" onClick={onShowSettings}>
                      <Settings size={16} />
                    </Button>
                  </Toolbar.Button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    style={{
                      backgroundColor: "var(--gray-12)",
                      color: "var(--gray-1)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                    sideOffset={5}
                  >
                    Workflow Settings
                    <Tooltip.Arrow style={{ fill: "var(--gray-12)" }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Flex>

            {(onEnableWorkflow || onDisableWorkflow) && (
              <>
                <Separator
                  orientation="vertical"
                  size="1"
                  style={{ margin: "0 4px", height: "24px" }}
                />
                <Flex align="center" gap="1" px="1">
                  <Toolbar.Button asChild>
                    <Button
                      variant="ghost"
                      size="1"
                      color={workflow.enabled ? "red" : "green"}
                      onClick={() =>
                        workflow.enabled
                          ? onDisableWorkflow?.()
                          : onEnableWorkflow?.()
                      }
                    >
                      {workflow.enabled ? "Disable" : "Enable"} Workflow
                    </Button>
                  </Toolbar.Button>
                </Flex>
              </>
            )}

            {isSaving && (
              <Flex align="end">
                <Text size="1" color="gray">
                  Saving...
                </Text>
              </Flex>
            )}
          </Flex>
        </Toolbar.Root>
      </Tooltip.Provider>
    </Flex>
  );
};
